import {
  MarketShippingConfiguration,
  MarketShippingOption,
  MarketShippingPreservationOptionSchema,
  MarketShippingRate,
  MarketShippingRateGroup,
} from "@intelligems/ig-types/dtos/market-shipping-configuration";
import { MarketShippingSelection } from "@intelligems/ig-types/dtos/shopify-shipping";
import { ShippingTestMethodDefinitionDto } from "@intelligems/ig-types/dtos/shipping-test-method-definitions";
import { analyzeMarketShippingPreservation } from "@intelligems/ig-utils/market-shipping-preservation";
import { currencyFormatter } from "@intelligems/ig-graphs/data-formatters";
import { isSameShippingSelection } from "@src/store/slices/experiences/shippingSelectionIdentity";
import { weightUnitLabel } from "./weightUnits";

/** Market targets of the market-model selections, in order. */
export const marketTargets = (
  selections: ShippingTestMethodDefinitionDto[]
): MarketShippingSelection[] =>
  selections.flatMap((item) =>
    item.shippingModel === "markets" ? [item.marketTarget] : []
  );

export const OPTION_TYPE_LABELS: Record<
  MarketShippingOption["optionType"],
  string
> = {
  flat_rate: "Flat rate",
  value_based: "Order amount",
  weight_based: "Order weight",
  carrier_calculated: "Carrier calculated",
  unsupported: "Unsupported",
};

/**
 * The option's name as the merchant knows it. Shopify gives carrier-calculated
 * options no name, so those read as their carrier services instead.
 */
export const optionDisplayName = (option: MarketShippingOption): string => {
  if (option.name) {
    return option.name;
  }
  const carriers = [
    ...new Set(
      option.rateGroups.flatMap((group) =>
        group.carrier?.carrierService.name
          ? [group.carrier.carrierService.name]
          : []
      )
    ),
  ];
  return carriers.length > 0
    ? carriers.join(", ")
    : OPTION_TYPE_LABELS[option.optionType];
};

/** Ids of every market that already has a saved selection. */
export const selectedMarketIds = (
  selections: ShippingTestMethodDefinitionDto[]
): string[] => [
  ...new Set(marketTargets(selections).map((target) => target.marketId)),
];

export const marketSelectionDefinition = (
  target: MarketShippingSelection
): ShippingTestMethodDefinitionDto => ({
  shippingModel: "markets",
  marketTarget: target,
});

export const marketUnavailableReason = (
  market: Pick<
    MarketShippingConfiguration,
    "status" | "shipping" | "eligibility"
  >
): string | null => {
  if (market.status !== "ACTIVE") {
    return "This market is inactive. Enable it in Shopify before testing.";
  }
  if (!market.shipping) {
    return "Shipping is inherited. Set separate shipping in Shopify before testing this market.";
  }
  if (!market.shipping.isEnabled) {
    return "Shipping is disabled for this market. Enable it in Shopify before testing.";
  }
  if (!market.eligibility.eligible) {
    return (
      market.eligibility.reason ||
      "This market is not available for shipping changes."
    );
  }
  return null;
};

export const selectionUnavailableReason = (
  target: MarketShippingSelection,
  market: MarketShippingConfiguration,
  completeSelections: MarketShippingSelection[] = [target]
): string | null => {
  const reason = marketUnavailableReason(market);
  if (reason) {
    return reason;
  }
  const option = market.shipping?.options.find(
    (item) => item.id === target.optionId
  );
  if (!option) {
    return "The saved shipping option no longer exists. Remove this selection and choose again.";
  }
  if (
    option.optionType === "unsupported" ||
    option.optionType !== target.optionType
  ) {
    return "This shipping option type has changed or is unsupported. Choose again.";
  }
  if (!option.isActive) {
    return "This shipping option is inactive.";
  }
  if (
    target.kind === "free_shipping_threshold" &&
    !option.freeDeliveryMinimumValue
  ) {
    return "The saved free-shipping threshold no longer exists.";
  }
  if (target.kind === "rate" || target.kind === "rate_group") {
    const group = option.rateGroups.find(
      (item) => item.id === target.rateGroupId
    );
    if (!group) {
      return "The saved product/location group no longer exists.";
    }
    if (
      target.kind === "rate" &&
      !group.rates.some((rate) => rate.id === target.rateId)
    ) {
      return "The saved rate no longer exists.";
    }
  }
  const analysis = analyzeMarketShippingPreservation(
    completeSelections.filter(
      (selection) =>
        selection.marketId === target.marketId &&
        selection.optionId === target.optionId
    ),
    MarketShippingPreservationOptionSchema.parse(option)
  );
  return analysis.eligible ? null : analysis.reason;
};

export const rateDescription = (
  rate: MarketShippingRate,
  locale?: string
): string => {
  const condition = rateConditionLabel(rate, locale);
  return condition
    ? `${ratePriceLabel(rate, locale)} · ${condition}`
    : ratePriceLabel(rate, locale);
};

export const selectionDescription = (
  target: MarketShippingSelection,
  option?: MarketShippingOption,
  locale?: string
): string => {
  if (target.kind === "option") {
    return "Whole shipping option";
  }
  if (target.kind === "free_shipping_threshold") {
    return `Free shipping over ${option?.freeDeliveryMinimumValue ? formatMarketMoney(option.freeDeliveryMinimumValue, locale) : "saved threshold"}`;
  }
  if (target.kind === "rate_group") {
    return `Rate group ${target.rateGroupId}`;
  }
  const rate = option?.rateGroups
    .find((group) => group.id === target.rateGroupId)
    ?.rates.find((item) => item.id === target.rateId);
  return rate ? rateDescription(rate, locale) : `Saved rate ${target.rateId}`;
};

export const formatMarketMoney = (
  value: { amount: string; currencyCode: string },
  locale?: string
) =>
  locale
    ? `${currencyFormatter(Number(value.amount), false, {
        code: value.currencyCode,
        locale,
      })} ${value.currencyCode}`
    : `${value.amount} ${value.currencyCode}`;

export const ratePriceLabel = (
  rate: MarketShippingRate,
  locale?: string
): string => formatMarketMoney(rate.price, locale);

/** Order-value or weight condition of a rate; null for unconditional rates. */
export const rateConditionLabel = (
  rate: MarketShippingRate,
  locale?: string
): string | null => {
  // "Order value $0.00 – $49.99 USD": the currency code once, at the end.
  if (rate.minValue) {
    return rate.maxValue
      ? `Order value ${formatMarketMoney(rate.minValue, locale).replace(/ \S+$/, "")} – ${formatMarketMoney(rate.maxValue, locale)}`
      : `Order value ${formatMarketMoney(rate.minValue, locale)} and up`;
  }
  if (rate.minWeight) {
    const unit = weightUnitLabel(rate.minWeight.unit);
    return rate.maxWeight
      ? `Weight ${rate.minWeight.value} – ${rate.maxWeight.value} ${weightUnitLabel(rate.maxWeight.unit)}`
      : `Weight ${rate.minWeight.value} ${unit} and up`;
  }
  return null;
};

const days = (seconds: number) =>
  `${seconds / 86400} ${seconds === 86400 ? "day" : "days"}`;

/** "3 days to 5 days"; null when neither bound is set. */
export const transitTimeLabel = (rate: MarketShippingRate): string | null => {
  if (
    rate.transitTimeMinSeconds === null &&
    rate.transitTimeMaxSeconds === null
  ) {
    return null;
  }
  const min =
    rate.transitTimeMinSeconds === null
      ? "Not set"
      : days(rate.transitTimeMinSeconds);
  const max =
    rate.transitTimeMaxSeconds === null
      ? "Not set"
      : days(rate.transitTimeMaxSeconds);
  return `${min} to ${max}`;
};

export const rateGroupProductsLabel = (
  group: MarketShippingRateGroup
): string =>
  group.conditions.collections === null
    ? "All products"
    : group.conditions.collections.map((item) => item.title).join(", ") ||
      "No collections";

export const rateGroupOriginsLabel = (group: MarketShippingRateGroup): string =>
  group.conditions.originLocations.map((item) => item.name).join(", ") ||
  "No locations listed";

export const carrierAdjustmentLabel = (
  carrier: NonNullable<MarketShippingRateGroup["carrier"]>,
  locale?: string
): string => {
  const parts = [
    carrier.absoluteAdjustment
      ? `${formatMarketMoney(carrier.absoluteAdjustment, locale)} adjustment`
      : null,
    carrier.percentageAdjustment !== null
      ? `${carrier.percentageAdjustment}% adjustment`
      : null,
  ].filter((part): part is string => part !== null);
  return parts.length ? parts.join(", ") : "No adjustment";
};

/**
 * Whether `target` is already selected and whether its checkbox must be
 * disabled: a selection is blocked when the option is unavailable, or when a
 * different selection on the same option already covers it (whole option vs
 * anything else; a rate group vs a rate inside it).
 */
export const selectionCheckboxState = (
  target: MarketShippingSelection,
  selections: ShippingTestMethodDefinitionDto[],
  blocked: boolean
): { checked: boolean; disabled: boolean } => {
  const definition = marketSelectionDefinition(target);
  const checked = selections.some((item) =>
    isSameShippingSelection(item, definition)
  );
  const overlapping = selections.some((item) => {
    if (
      item.shippingModel !== "markets" ||
      item.marketTarget.marketId !== target.marketId ||
      item.marketTarget.optionId !== target.optionId ||
      isSameShippingSelection(item, definition)
    ) {
      return false;
    }
    const other = item.marketTarget;
    return (
      other.kind === "option" ||
      target.kind === "option" ||
      (other.rateGroupId === target.rateGroupId &&
        Boolean(target.rateGroupId) &&
        (other.kind === "rate_group" || target.kind === "rate_group"))
    );
  });
  return { checked, disabled: !checked && (blocked || overlapping) };
};
